/**
 * The tuning, key, and voicing chooser reached through the score header.
 *
 * Each choice rewrites the exercise, so it confirms first when there is work to
 * lose. Majors and minors are listed separately because the mode is the part a
 * student is choosing and its signature accidentals follow from it.
 */

import { useState, type JSX } from 'react';
import { createExercise, edoOf, keyById, keysOfMode, type CfPosition, type Edo } from '../core';
import { useSession } from '../state/store';
import { Modal } from './Modal';

export function KeyDialog({
  open,
  onClose,
  onConfirmNeeded,
}: {
  open: boolean;
  onClose: () => void;
  /** Asks the host to confirm before discarding written work. */
  onConfirmNeeded: (apply: () => void) => void;
}): JSX.Element {
  const exercise = useSession((s) => s.exercise);
  const settings = useSession((s) => s.settings);
  const loadExercise = useSession((s) => s.loadExercise);
  const [cfPos, setCfPos] = useState<CfPosition>(exercise.cfPos);

  const apply = (keyId: string, edo: Edo = edoOf(exercise)) => {
    const key = keyById(keyId);
    const build = () => {
      loadExercise(
        createExercise(key, cfPos, settings.profile, settings.layout === 'single', edo),
        { edo, keyPreference: keyId, cfPreference: cfPos },
      );
      onClose();
    };
    onConfirmNeeded(build);
  };

  return (
    <Modal open={open} title="Tuning, key, and voicing" onClose={onClose}>
      <p className="modal__lede">
        Each choice generates a fresh cantus firmus because the exercise is built around its tuning, key, and voicing.
      </p>

      <fieldset className="field">
        <legend>Equal division of the octave</legend>
        <div className="segmented">
          {([12, 31] as const).map((edo) => (
            <button
              key={edo}
              type="button"
              className={edoOf(exercise) === edo ? 'segmented__item segmented__item--on' : 'segmented__item'}
              aria-pressed={edoOf(exercise) === edo}
              onClick={() => {
                if (edo !== edoOf(exercise)) apply(exercise.key.id, edo);
              }}
            >
              {edo}-EDO
            </button>
          ))}
        </div>
        <p className="field__note">
          31-EDO uses circle-of-fifths spellings and traverses every pitch class with the up and down arrows.
        </p>
      </fieldset>

      <fieldset className="field">
        <legend>Where the cantus firmus sits</legend>
        <div className="segmented">
          {(
            [
              ['lower', 'Below you'],
              ['upper', 'Above you'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={cfPos === value ? 'segmented__item segmented__item--on' : 'segmented__item'}
              aria-pressed={cfPos === value}
              onClick={() => setCfPos(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      {(['major', 'minor'] as const).map((mode) => (
        <fieldset className="field" key={mode}>
          <legend>{mode === 'major' ? 'Major' : 'Minor'}</legend>
          <div className="keygrid">
            {keysOfMode(mode).map((k) => (
              <button
                key={k.id}
                type="button"
                className={exercise.key.id === k.id ? 'keygrid__item keygrid__item--on' : 'keygrid__item'}
                onClick={() => apply(k.id)}
              >
                {k.label}
              </button>
            ))}
          </div>
        </fieldset>
      ))}
    </Modal>
  );
}
